import { useEffect, useState } from 'react';

import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';
import { todasAsLinhas } from '@/components/atomic-crm/providers/supabase/todasAsLinhas';
import { useMascara } from '../valores-ocultos/contexto';

/**
 * Tendência da inadimplência (30 dias) — morava no card do Início e veio pra
 * cá no v3: o dashboard mostra o número e a porta; a página mostra a história.
 *
 * Herda a regra do antigo BlocoTendenciaCarteira: o snapshot diário
 * (servopa_vendas_daily_snapshot) começou em 2026-08-08, e com poucos dias
 * uma "tendência" é ruído desenhado com confiança. Só aparece com 14+ dias
 * CAPTURADOS (todas as linhas contam — dia sem atraso é dia medido); antes
 * disso o card diz que está juntando histórico.
 */
const JANELA_DIAS = 30;
const DIAS_MINIMOS = 14;

export const TendenciaInadimplencia = () => {
  const [serie, setSerie] = useState<number[] | null>(null);
  const m = useMascara();

  useEffect(() => {
    let ativo = true;
    (async () => {
      // O snapshot tem ~1.000 linhas POR DIA: ler a janela inteira passava das
      // 1000 que o PostgREST devolve e o card nunca aparecia. Agora: só as
      // linhas inadimplentes (poucas, em páginas) + uma contagem por dia para
      // saber quais dias foram capturados.
      const dias = Array.from({ length: JANELA_DIAS + 1 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - JANELA_DIAS + i);
        return d.toISOString().slice(0, 10);
      });
      const [inadimplentes, ...contagens] = await Promise.all([
        todasAsLinhas<{ snapshot_date: string; venda_id: string }>(
          () =>
            supabase
              .from('servopa_vendas_daily_snapshot')
              .select('snapshot_date, venda_id')
              .eq('is_inadimplente', true)
              .neq('status', 'anulada')
              .gte('snapshot_date', dias[0])
              .order('snapshot_date', { ascending: true }),
          'venda_id',
        ),
        ...dias.map((dia) =>
          supabase
            .from('servopa_vendas_daily_snapshot')
            .select('venda_id', { count: 'exact', head: true })
            .eq('snapshot_date', dia),
        ),
      ]);
      if (!ativo) return;
      const diasCapturados = dias.filter((_, i) => (contagens[i].count ?? 0) > 0);
      if (diasCapturados.length < DIAS_MINIMOS) {
        setSerie([]);
        return;
      }
      const porDia = new Map<string, number>();
      for (const dia of diasCapturados) porDia.set(dia, 0);
      for (const r of inadimplentes.data ?? []) {
        if (porDia.has(r.snapshot_date)) porDia.set(r.snapshot_date, (porDia.get(r.snapshot_date) ?? 0) + 1);
      }
      setSerie([...porDia.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, n]) => n));
    })();
    return () => {
      ativo = false;
    };
  }, []);

  const spark = (() => {
    if (!serie || serie.length < 2) return null;
    const max = Math.max(...serie, 1);
    const min = Math.min(...serie);
    const range = Math.max(1, max - min);
    return serie
      .map((v, i) => {
        const x = (i * 120) / (serie.length - 1);
        const y = 28 - ((v - min) * 24) / range;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');
  })();

  const primeiro = serie?.[0] ?? 0;
  const ultimo = serie?.[serie.length - 1] ?? 0;
  const variacao = serie && serie.length > 1 ? ultimo - primeiro : null;

  return (
    <div className="cartao bg-card px-4 py-3.5">
      <p className="truncate text-[12.5px] font-semibold text-[#6b7480] dark:text-muted-foreground">
        Tendência (30 dias)
      </p>
      {spark ? (
        <>
          <svg viewBox="0 0 120 32" preserveAspectRatio="none" className="mt-2.5 block h-8 w-full">
            <polyline
              points={spark}
              fill="none"
              stroke="var(--primary)"
              strokeOpacity="0.55"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <p className="mt-1.5 truncate text-[11.5px] text-[#6b7480] dark:text-muted-foreground">
            contratos em atraso, dia a dia
            {variacao != null && variacao !== 0 && (
              <>
                {' · '}
                <span
                  className={
                    variacao < 0 ? 'font-semibold text-[#166534]' : 'font-semibold text-[#b45309]'
                  }
                >
                  {m(`${variacao > 0 ? '+' : ''}${variacao}`)} no período
                </span>
              </>
            )}
          </p>
        </>
      ) : (
        <p className="mt-2 text-[11.5px] text-[#6b7480] dark:text-muted-foreground">
          {serie == null
            ? '…'
            : `Histórico em formação. O gráfico é exibido a partir de ${DIAS_MINIMOS} dias de medição.`}
        </p>
      )}
    </div>
  );
};
